import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useAuth } from "react-oidc-context";
import { sortHotels, getNextSortDirection } from "../../Home/utils/homeUtils";
import { isAbortError, isFresh, isLoadingKey, mergeHotelList, mergeHotelPatches, readCache, runExclusive, setCacheData, setCacheFetched } from "../../Home/hooks/hotelsCache";

const API_URL = process.env.REACT_APP_API_URL || "/api";
const CACHE_KEY = "lunishower:hotels";

export const useLuniShowerHotels = () => {
  const [hotels, setHotels] = useState(() => readCache(CACHE_KEY)?.data ?? []);
  const [loading, setLoading] = useState(() => !readCache(CACHE_KEY));
  const [statsPendingIds, setStatsPendingIds] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortState, setSortState] = useState({
    column: null,
    direction: "asc",
  });

  const auth = useAuth();

  // Lu au moment de la requête : un renouvellement silencieux du token ne doit ni changer
  // l'identité des callbacks ni relancer les chargements.
  const tokenRef = useRef(auth.user?.access_token);
  useEffect(() => {
    tokenRef.current = auth.user?.access_token;
  });

  const hotelsRef = useRef(hotels);

  const requestConfig = useCallback(
    (signal) => ({
      headers: { Authorization: `Bearer ${tokenRef.current}` },
      ...(signal ? { signal } : {}),
    }),
    []
  );

  /** Point de passage unique : ref, cache, et `setHotels` seulement si la liste change. */
  const commitHotels = useCallback((updater, { fetched = false } = {}) => {
    const previous = hotelsRef.current;
    const next = typeof updater === "function" ? updater(previous) : updater;

    hotelsRef.current = next;
    if (fetched) {
      setCacheFetched(CACHE_KEY, next);
    } else {
      setCacheData(CACHE_KEY, next);
    }

    if (next !== previous) setHotels(next);
  }, []);

  const markStatsPending = useCallback((hotelIds) => {
    setStatsPendingIds((previous) => {
      const next = new Set(previous);
      hotelIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const clearStatsPending = useCallback((hotelIds) => {
    setStatsPendingIds((previous) => {
      if (previous.size === 0) return previous;
      const next = new Set(previous);
      hotelIds.forEach((id) => next.delete(id));
      return next.size === previous.size ? previous : next;
    });
  }, []);

  /**
   * Charge les stats pour plusieurs hôtels et met à jour la liste.
   *
   * L'ancien garde `!loadingStats` capturait un `loadingStats` périmé dans la closure et
   * pouvait sauter silencieusement le chargement des stats. La déduplication se fait
   * désormais par clé, hors du cycle de rendu.
   */
  const fetchHotelsStats = useCallback(
    async (hotelIds, { signal, pendingIds } = {}) => {
      if (!hotelIds || hotelIds.length === 0) return;

      // `pendingIds` permet à une revalidation de fond de ne pas repasser en placeholder
      // des lignes déjà affichées.
      const placeholderIds = pendingIds ?? hotelIds;
      markStatsPending(placeholderIds);
      try {
        const response = await axios.post(`${API_URL}/home/stats`, { hotelIds }, requestConfig(signal));
        if (signal?.aborted) return;
        commitHotels((previous) => mergeHotelPatches(previous, response.data.result || {}));
      } catch (error) {
        if (isAbortError(error)) return;
        console.error("Erreur lors de la récupération des stats des hôtels:", error);
        // Ne pas bloquer l'affichage si les stats échouent
      } finally {
        clearStatsPending(placeholderIds);
      }
    },
    [commitHotels, markStatsPending, clearStatsPending, requestConfig]
  );

  /**
   * Charge la liste light LuniShower puis les stats.
   *
   * @param {AbortSignal} signal      annule tout au démontage
   * @param {boolean}     background  revalidation silencieuse (liste déjà affichée)
   */
  const loadHotels = useCallback(
    async ({ signal, background = false } = {}) => {
      if (background && isLoadingKey(CACHE_KEY)) return;

      if (!background) setLoading(true);
      setError(null);

      try {
        await runExclusive(CACHE_KEY, async () => {
          // Endpoint light dédié LuniShower : pas de JOIN GreenMiles/LuniButton, uniquement hôtels avec manager_id
          const response = await axios.get(`${API_URL}/home/list/lunishower`, requestConfig(signal));
          if (signal?.aborted) return;

          const lunishowerHotels = response.data.result || [];
          const alreadyDisplayedIds = new Set(hotelsRef.current.map((hotel) => hotel.id));

          commitHotels((previous) => (previous.length > 0 ? mergeHotelList(previous, lunishowerHotels) : lunishowerHotels), { fetched: true });
          setLoading(false);

          if (lunishowerHotels.length === 0) return;

          const hotelIds = lunishowerHotels.map((hotel) => hotel.id);
          await fetchHotelsStats(hotelIds, {
            signal,
            // En revalidation, ne pas vider les lignes déjà affichées.
            pendingIds: background ? hotelIds.filter((id) => !alreadyDisplayedIds.has(id)) : hotelIds,
          });
        });
      } catch (error) {
        if (isAbortError(error)) return;
        console.error("Erreur lors de la récupération de la liste des hôtels LuniShower:", error);
        setError("Erreur lors du chargement des hôtels LuniShower");
        setLoading(false);
      }
    },
    [commitHotels, fetchHotelsStats, requestConfig]
  );

  // Chargement au montage : dépendances toutes stables, aucun `eslint-disable` nécessaire.
  useEffect(() => {
    const controller = new AbortController();
    const entry = readCache(CACHE_KEY);

    if (entry) {
      setLoading(false);
      if (!isFresh(entry)) loadHotels({ signal: controller.signal, background: true });
    } else {
      loadHotels({ signal: controller.signal });
    }

    return () => controller.abort();
  }, [loadHotels]);

  const fetchHotels = useCallback(async () => {
    await loadHotels();
  }, [loadHotels]);

  // managerId = id manager (pour les scripts), masterId = id master lunidata_v2 (pour refetch stats)
  const updateHotelStatus = useCallback(
    async (managerId, masterId) => {
      try {
        await axios.post(`${API_URL}/scripts/updateStatus/hotel/${managerId}`, {}, requestConfig());
        await fetchHotelsStats([masterId ?? managerId]);
      } catch (error) {
        console.error("Erreur lors de la mise à jour des statuts:", error);
        throw error;
      }
    },
    [fetchHotelsStats, requestConfig]
  );

  const handleSort = useCallback((column) => {
    setSortState((previous) => ({ column, direction: getNextSortDirection(previous.column, column, previous.direction) }));
  }, []);

  // Filtrer les hôtels en local selon la recherche
  const filteredHotels = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return hotels;

    return hotels.filter((hotel) => {
      const name = hotel.name?.toLowerCase() || "";
      const tag = hotel.tag?.toLowerCase() || "";
      return name.includes(term) || tag.includes(term);
    });
  }, [hotels, searchTerm]);

  // Appliquer le tri aux hôtels filtrés. Mémoïsé comme `filteredHotels` juste au-dessus :
  // sans cela, tout le tableau était retrié à chaque render.
  const sortedHotels = useMemo(() => sortHotels(filteredHotels, sortState.column, sortState.direction), [filteredHotels, sortState.column, sortState.direction]);

  return {
    hotels: sortedHotels,
    loading,
    loadingStats: statsPendingIds.size > 0,
    statsPendingIds,
    error,
    searchTerm,
    setSearchTerm,
    fetchHotels,
    updateHotelStatus,
    sortState,
    handleSort,
    refetch: fetchHotels,
  };
};
